import { api } from '../api.js';
import { escapeHtml } from '../../../../src/shared/escape-html.js';

const SYSTEM_LABELS = { nes: 'NES', snes: 'SNES', genesis: 'Genesis', gb: 'GB', gbc: 'GBC', gba: 'GBA', atari2600: 'Atari', c64: 'C64' };

const REASON_LABELS = {
  no_leaderboard: 'No leaderboard',
  no_score_format: 'No SCORE/VALUE format',
  no_val_expression: 'No VAL expression',
  no_local_rom: 'Parseable but no local ROM',
  addsource_unsupported: 'AddSource (unsupported pattern)',
  measured_complex: 'Measured (complex)',
  chain_unsupported: 'Chain (unsupported pattern)',
  alt_groups: 'Alt groups ($)',
  control_flow: 'AndNext / OrNext',
  delta: 'Delta',
  recall: 'Recall (K:)',
};

/** A server count as text: a non-negative integer, else '?' (never interpolated unchecked into HTML). */
const countText = (value) => (Number.isSafeInteger(value) && value >= 0 ? String(value) : '?');

let _scoreGames = [];
let _missingGames = [];
let _bound = false;

// ── Score tracking ───────────────────────────────────────

async function renderScoreTracking() {
  const body = document.getElementById('admin-scores-body');
  const stats = document.getElementById('admin-scores-stats');
  if (!body) return;
  body.innerHTML = '<tr><td colspan="8" class="admin-loading">Loading…</td></tr>';

  try {
    const data = await api.get('/api/admin/score-tracking');
    _scoreGames = data.games || [];

    if (stats) {
      const bySys = {};
      for (const g of _scoreGames) bySys[g.system] = (bySys[g.system] || 0) + 1;
      const authored = data.authored?.state === 'ready' && Number.isInteger(data.authored.count)
        ? ` (${data.authored.count} authored)`
        : (data.authored ? ' (authored definitions are not available on this server right now)' : '');
      stats.innerHTML = `<strong>${countText(data.total)}</strong> games with score tracking${authored} · ` +
        Object.entries(bySys).map(([s, c]) => `${escapeHtml(SYSTEM_LABELS[s] || s)}: ${c}`).join(', ');
    }

    renderScoreRows(_scoreGames);
  } catch {
    body.innerHTML = '<tr><td colspan="8" class="admin-loading">Could not load score tracking data</td></tr>';
  }
}

function renderScoreRows(games) {
  const body = document.getElementById('admin-scores-body');
  if (!body) return;
  body.innerHTML = '';

  for (const g of games) {
    const row = document.createElement('tr');
    const coverSrc = g.gameId ? `/api/library/artwork/cover/${encodeURIComponent(g.gameId)}` : '';
    // Sum-of-terms specs have no top-level addr (the addresses live in terms[]);
    // show a marker instead of crashing on `.toString` of undefined.
    const addr = g.scoreSpec.addr != null
      ? `0x${g.scoreSpec.addr.toString(16).toUpperCase()}`
      : (g.scoreSpec.format === 'terms' ? `${g.scoreSpec.terms?.length || 0} terms` : '–');
    const mul = g.scoreSpec.multiplier ? ` ×${g.scoreSpec.multiplier}` : '';
    row.innerHTML =
      `<td><img src="${coverSrc}" alt="" style="width:32px;height:43px;object-fit:cover;border-radius:3px;background:#1a1a28" onerror="this.style.visibility='hidden'"></td>` +
      `<td>${escapeHtml(g.title)}</td>` +
      `<td>${escapeHtml(SYSTEM_LABELS[g.system] || g.system)}</td>` +
      `<td>${escapeHtml(g.scoreSpec.format)}${escapeHtml(mul)}</td>` +
      `<td><code>${escapeHtml(addr)}</code></td>` +
      `<td>${escapeHtml(g.scoreSpec.len)}</td>` +
      `<td>${escapeHtml(g.scoreSpec.endian)}</td>` +
      `<td>${g.source === 'authored' ? 'Authored' : 'Static'}</td>`;
    row.dataset.system = g.system;
    row.dataset.title = g.title.toLowerCase();
    body.appendChild(row);
  }
}

function filterScoreRows() {
  const search = (document.getElementById('admin-scores-search')?.value || '').toLowerCase();
  const system = document.getElementById('admin-scores-system')?.value || '';
  const filtered = _scoreGames.filter(g => {
    if (system && g.system !== system) return false;
    if (search && !g.title.toLowerCase().includes(search)) return false;
    return true;
  });
  renderScoreRows(filtered);
}

// ── Missing scores ──────────────────────────────────────

async function renderMissingScores() {
  const body = document.getElementById('admin-missing-body');
  const stats = document.getElementById('admin-missing-stats');
  if (!body) return;
  body.innerHTML = '<tr><td colspan="2" class="admin-loading">Loading…</td></tr>';

  try {
    const data = await api.get('/api/admin/score-coverage');
    _missingGames = data.games || [];

    if (stats) {
      const byReason = {};
      for (const g of _missingGames) byReason[g.reason] = (byReason[g.reason] || 0) + 1;
      const sorted = Object.entries(byReason).sort((a, b) => b[1] - a[1]);
      const authoredTracked = Number.isInteger(data.authored_tracked) ? `, plus ${data.authored_tracked} authored` : '';
      stats.innerHTML = `<strong>${countText(data.missing)}</strong> games missing score tracking (${countText(data.tracked)} tracked${authoredTracked})<br>` +
        sorted.map(([r, c]) => `${escapeHtml(REASON_LABELS[r] || r)}: ${c}`).join(' · ');
    }

    const select = document.getElementById('admin-missing-reason');
    if (select) {
      const reasons = [...new Set(_missingGames.map(g => g.reason))].sort();
      for (const r of reasons) {
        const opt = document.createElement('option');
        opt.value = r;
        opt.textContent = REASON_LABELS[r] || r;
        select.appendChild(opt);
      }
    }

    renderMissingRows(_missingGames);
  } catch {
    body.innerHTML = '<tr><td colspan="2" class="admin-loading">Could not load coverage data</td></tr>';
  }
}

function renderMissingRows(games) {
  const body = document.getElementById('admin-missing-body');
  if (!body) return;
  body.innerHTML = '';

  for (const g of games) {
    const row = document.createElement('tr');
    row.innerHTML =
      `<td>${escapeHtml(g.title)}</td>` +
      `<td>${escapeHtml(REASON_LABELS[g.reason] || g.reason)}</td>`;
    row.dataset.reason = g.reason;
    row.dataset.title = g.title.toLowerCase();
    body.appendChild(row);
  }
}

function filterMissingRows() {
  const search = (document.getElementById('admin-missing-search')?.value || '').toLowerCase();
  const reason = document.getElementById('admin-missing-reason')?.value || '';
  const filtered = _missingGames.filter(g => {
    if (reason && g.reason !== reason) return false;
    if (search && !g.title.toLowerCase().includes(search)) return false;
    return true;
  });
  renderMissingRows(filtered);
}

// ── Render entry point ──────────────────────────────────

export function render() {
  if (!_bound) {
    _bound = true;

    const scoresSearch = document.getElementById('admin-scores-search');
    const scoresSystem = document.getElementById('admin-scores-system');
    if (scoresSearch) scoresSearch.addEventListener('input', () => filterScoreRows());
    if (scoresSystem) scoresSystem.addEventListener('change', () => filterScoreRows());

    const missingSearch = document.getElementById('admin-missing-search');
    const missingReason = document.getElementById('admin-missing-reason');
    if (missingSearch) missingSearch.addEventListener('input', () => filterMissingRows());
    if (missingReason) missingReason.addEventListener('change', () => filterMissingRows());
  }

  renderScoreTracking();
  renderMissingScores();
}
